<template lang="pug">
.bq-dashboard

    //- editable fields
    .bq-meta(:class="{ disabled: !user?.email_verified || currentService.service.active < 0 }")
        h2.bq-section-title Project

        .bq-row
            .bq-toggle-label
                span Name
                small.bq-toggle-hint(translate="no") {{ currentService.service.name }}

            a.bq-link(
                @click="editName"
                :class="{ disabled: !user?.email_verified || currentService.service.active < 0 }"
            ) [Edit]

        .bq-row
            .bq-toggle-label
                span Description
                small.bq-toggle-hint(v-if="currentService.service.description" translate="no") {{ currentService.service.description }}
                small.bq-toggle-hint(v-else) No description

            a.bq-link(
                @click="editDescription"
                :class="{ disabled: !user?.email_verified || currentService.service.active < 0 }"
            ) [Edit]

    .bq-meta
        h2.bq-section-title Information

        //- The id is what every SDK call starts with, so the whole value copies
        //- (the Web Hosting address's glyph language).
        .bq-meta-row
            span Project ID
            CopyText.bq-meta-copy(:value="currentService.service.projectId")
                span.bq-mono.bq-meta-copy-value(translate="no") {{ currentService.service.projectId }}
                span.material-symbols-outlined.bq-share-copy-icon(translate="no" aria-hidden="true") content_copy
        .bq-meta-row
            span Owner ID
            span.bq-mono(translate="no") {{ currentService.service.owner }}
        .bq-meta-row
            span Service ID
            span.bq-mono(translate="no") {{ currentService.service.service }}
        .bq-meta-row
            span Region
            span.bq-mono(translate="no") {{ regionText(currentService.service.region) }}
        .bq-meta-row
            span Created
            span {{ dateFormat(currentService.dateCreated) }}
        .bq-meta-row
            span Documentation
            //- Opens the docs in a new tab, which the glyph says (the Web Hosting
            //- page's [Open] uses the same one).
            a.bq-cta-link(:href="PROJECT_DOCS.gettingStarted" target="_blank" rel="noopener")
                | Getting Started
                span.material-symbols-outlined.bq-meta-doc-icon(translate="no" aria-hidden="true") open_in_new

    //- Ready-made pages on the CDN that run against this project (components/templateLinks.vue).
    TemplateLinks(
        :project-id="currentService.service.projectId"
        :disabled="currentService.service.active < 0"
    )

    //- The base prompt for AI coding agents, named per tool (components/agentPrompts.vue).
    AgentPrompts(:project-id="currentService.service.projectId")

    //- plan + usage
    .bq-meta
        h2.bq-section-title Plan &amp; Usage
        .bq-plan-header
            span.bq-badge(:class="currentService.formattedSpecs.currentPlan.label.toLowerCase().split(' ')[0]") {{ currentService.formattedSpecs.currentPlan.label }}
            span.bq-renew(v-if="currentService.subscription?.current_period_end && currentService.formattedSpecs.currentPlan.label.toLowerCase() !== 'free' && currentService.formattedSpecs.serviceStatus.canChangePlan && !currentService.subscription?.will_cancel && currentService.service.active === 1 && currentService.subscription?.status !== 'past_due'") Renews {{ dateFormat(currentService.subscription?.current_period_end * 1000) }}

        .bq-usage-bars
            .bq-usage-item
                .bq-usage-head
                    span Users
                    span {{ currentService.formattedSpecs.serviceStatus.users_string || 0 }} / {{ currentService.formattedSpecs.currentPlan.users_string }}
                .bq-bar
                    .bq-bar-fill(:style="{ width: barWidth(currentService.formattedSpecs.serviceStatus.users_percent) }" :class="barColor(currentService.formattedSpecs.serviceStatus.users_percent)")

            .bq-usage-item
                .bq-usage-head
                    span Database
                    span {{ currentService.formattedSpecs.serviceStatus.storage.database }} / {{ currentService.formattedSpecs.currentPlan.storage.database }}
                .bq-bar
                    .bq-bar-fill(:style="{ width: barWidth(currentService.formattedSpecs.serviceStatus.storage.database_percent) }" :class="barColor(currentService.formattedSpecs.serviceStatus.storage.database_percent)")

            //- One bar for the file pool: hosting, cloud files and file storage
            //- share the plan's ceiling, so they are drawn as three coloured
            //- segments of the same track, and the legend names each share.
            .bq-usage-item
                .bq-usage-head
                    span File Storage
                    span(translate="no") {{ currentService.formattedSpecs.serviceStatus.storage.pool }} / {{ currentService.formattedSpecs.currentPlan.storage.pool }}
                .bq-bar.bq-bar--stacked(:class="poolTrackClass(currentService.formattedSpecs.serviceStatus.storage.pool_percent)")
                    .bq-bar-seg.bq-bar-seg--host(:style="{ width: currentService.formattedSpecs.serviceStatus.storage.segments.host.width }")
                    .bq-bar-seg.bq-bar-seg--cloud(:style="{ width: currentService.formattedSpecs.serviceStatus.storage.segments.cloud.width }")
                    .bq-bar-seg.bq-bar-seg--file(:style="{ width: currentService.formattedSpecs.serviceStatus.storage.segments.file.width }")
                .bq-usage-legend
                    span.bq-legend-item
                        i.bq-legend-dot.bq-bar-seg--host
                        | Hosting
                        span(translate="no") {{ currentService.formattedSpecs.serviceStatus.storage.segments.host.text }}
                    span.bq-legend-item
                        i.bq-legend-dot.bq-bar-seg--cloud
                        | Cloud storage
                        span(translate="no") {{ currentService.formattedSpecs.serviceStatus.storage.segments.cloud.text }}
                    span.bq-legend-item
                        i.bq-legend-dot.bq-bar-seg--file
                        | File storage
                        span(translate="no") {{ currentService.formattedSpecs.serviceStatus.storage.segments.file.text }}

            //- Metered per MONTH, unlike the levels above. Drawn only when the
            //- plan quotes a ceiling: egress_bytes is 0 for Unlimited and NaN
            //- until GET /plan answers, and neither is a bar.
            .bq-usage-item(v-if="currentService.formattedSpecs.currentPlan.egress_bytes > 0")
                .bq-usage-head
                    span Bandwidth (this month)
                    span(translate="no") {{ currentService.formattedSpecs.serviceStatus.usage_month.egress }} / {{ currentService.formattedSpecs.currentPlan.egress }}
                .bq-bar
                    .bq-bar-fill(:style="{ width: barWidth(currentService.formattedSpecs.serviceStatus.usage_month.egress_percent) }" :class="barColor(currentService.formattedSpecs.serviceStatus.usage_month.egress_percent)")
                //- Metered now; enforcement and Premium billing ship switched off
                //- (GET /plan switches), so say which one is still off.
                .bq-usage-legend(v-if="bandwidthMeterNote(currentService.formattedSpecs.currentPlan.hard_stop)")
                    span
                        | {{ bandwidthMeterNote(currentService.formattedSpecs.currentPlan.hard_stop) + " " }}
                        a.bq-cta-link(:href="PLAN_DOCS.bandwidth" target="_blank" rel="noopener" style='white-space: nowrap;') Learn more

            .bq-usage-item(v-if="currentService.formattedSpecs.currentPlan.email_sends > 0")
                .bq-usage-head
                    span Email sends (this month)
                    span(translate="no") {{ currentService.formattedSpecs.serviceStatus.usage_month.email_sends }} / {{ currentService.formattedSpecs.currentPlan.email_sends_string }}
                .bq-bar
                    .bq-bar-fill(:style="{ width: barWidth(currentService.formattedSpecs.serviceStatus.usage_month.email_sends_percent) }" :class="barColor(currentService.formattedSpecs.serviceStatus.usage_month.email_sends_percent)")
                //- One line: a block in force (spam complaints, sends used up),
                //- else how a send is counted. The rest of the rules (the
                //- per-owner cap, the complaint shutoff) are behind the link
                //- (code/plan-copy.ts emailSendsMeterNote).
                .bq-usage-legend
                    span(:class="{ 'bq-usage-alert': sendsNote.danger }")
                        | {{ sendsNote.text + " " }}
                        a.bq-cta-link(:href="sendsNote.href" target="_blank" rel="noopener" style='white-space: nowrap;') Learn more

            //- .bq-usage-item
            //-     .bq-usage-head
            //-         span Cloud Storage
            //-         span {{ currentService.formattedSpecs.serviceStatus.storage.cloud }} / {{ currentService.formattedSpecs.currentPlan.storage.cloud }}
            //-     .bq-bar
            //-         .bq-bar-fill(:style="{ width: barWidth(currentService.formattedSpecs.serviceStatus.storage.cloud_percent) }" :class="barColor(currentService.formattedSpecs.serviceStatus.storage.cloud_percent)")

            //- Email storage: the oldest newsletters are deleted when it is full on
            //- Free and Standard, the excess is billed on Premium. Drawn wherever
            //- the plan has a ceiling for it (a region still serving 0 draws it
            //- against the amount it suspends above).
            .bq-usage-item(v-if="currentService.formattedSpecs.currentPlan.storage.email !== 'Unlimited' && (currentService.formattedSpecs.currentPlan.storage_bytes.email > 0 || currentService.formattedSpecs.serviceStatus.storage.email_bytes > 0)")
                .bq-usage-head
                    span Email Storage
                    span(translate="no") {{ currentService.formattedSpecs.serviceStatus.storage.email }} / {{ currentService.formattedSpecs.serviceStatus.storage.email_limit_zero ? 'none included' : currentService.formattedSpecs.currentPlan.storage.email }}
                .bq-bar
                    .bq-bar-fill(:style="{ width: barWidth(currentService.formattedSpecs.serviceStatus.storage.email_percent) }" :class="barColor(currentService.formattedSpecs.serviceStatus.storage.email_percent)")
                //- What happens when it is full, for this plan; the rule for
                //- every plan is behind the link (code/plan-copy.ts
                //- emailStorageMeterNote).
                .bq-usage-legend
                    span
                        | {{ storageNote.text + " " }}
                        a.bq-cta-link(:href="storageNote.href" target="_blank" rel="noopener" style='white-space: nowrap;') Learn more

            //- .bq-usage-item
            //-     .bq-usage-head
            //-         span Hosting
            //-         span {{ currentService.formattedSpecs.serviceStatus.storage.host }} / {{ currentService.formattedSpecs.currentPlan.storage.host }}
            //-     .bq-bar
            //-         .bq-bar-fill(:style="{ width: barWidth(currentService.formattedSpecs.serviceStatus.storage.host_percent) }" :class="barColor(currentService.formattedSpecs.serviceStatus.storage.host_percent)")

            //- The alignment lives on the wrapper, not on the link: as a
            //- right-aligned BLOCK the link stretched the full card width, so
            //- the empty space left of the button was clickable too.
            .bq-plan-cta
                router-link(
                    :to="`/subscription/${currentService.id}`"
                )
                    button {{ planCtaLabel(currentService) }}

        //- A suspended project: which limits are over, by how much, and where the
        //- data can be deleted (code/plan-copy.ts suspendedLimitRows).
        .bq-overage(v-if="suspendRows.length")
            h3.bq-overage-title Over the limit
            .bq-row(v-for="r in suspendRows" :key="r.key")
                .bq-toggle-label
                    span {{ STORED_LIMIT_LABEL[r.key] }}
                    small.bq-toggle-hint(translate="no") {{ r.text }}
                    small.bq-toggle-hint(v-if="r.hint") {{ r.hint }}
                router-link.bq-link(v-if="r.action?.to" :to="r.action.to") [{{ r.action.label }}]
                a.bq-link(v-else-if="r.action?.href" :href="r.action.href" target="_blank" rel="noopener") [{{ r.action.label }}]

        //- What happens past a limit. A plan without a hard stop is billed for
        //- the excess, and the OverageMeter job writes the month to date onto
        //- the service row (service.ovg); a hard stop plan just pauses. Unlimited
        //- has no limits to pass, so it draws neither block.
        .bq-overage(v-if="currentService.formattedSpecs.currentPlan.hard_stop === false && currentService.formattedSpecs.currentPlan.label !== 'Unlimited'")
            h3.bq-overage-title Overage (month to date)
            template(v-if="currentService.formattedSpecs.serviceStatus.overage")
                .bq-meta-row(v-for="row in currentService.formattedSpecs.serviceStatus.overage.rows" :key="row.metric")
                    span {{ row.label }}
                    span(translate="no") {{ row.over }} over, {{ row.cost }}
                .bq-meta-row.bq-overage-total
                    span Total
                    span(translate="no") {{ currentService.formattedSpecs.serviceStatus.overage.total }}
                p.bq-overage-note
                    | {{ currentService.formattedSpecs.serviceStatus.overage.billable ? "Billed with your next invoice" : "Not billed on this plan" }}
                    template(v-if="currentService.formattedSpecs.serviceStatus.overage.sampled")
                        | , sampled&nbsp;
                        span(translate="no") {{ currentService.formattedSpecs.serviceStatus.overage.sampled }}
            p.bq-overage-note(v-else) No overage recorded yet
        p.bq-overage-note.bq-overage-note--alone(v-else-if="currentService.formattedSpecs.currentPlan.hard_stop !== false")
            | {{ hardStopNote() + " " }}
            a.bq-cta-link(:href="PLAN_DOCS.hardStop" target="_blank" rel="noopener" style='white-space: nowrap;') Learn more
    //- The project's hosting address. It is the one thing an owner comes to
    //- this page to hand to somebody else, so it leads the page with its own
    //- copy button. The Web Hosting page uploads files; the address itself is
    //- registered, changed and removed here and nowhere else.
    section#settings-hosting.bq-share(v-if="hostingSubdomain")
        .bq-share-head
            span.bq-share-title Web Hosting
            small.bq-share-hint Your site is served from this address. Upload files on the Web Hosting page. #[a.bq-cta-link(:href="PROJECT_DOCS.hosting" target="_blank" rel="noopener" style='white-space: nowrap;') Learn more]
        .bq-share-bar
            //- The address opens the site; the glyph beside it copies it. The
            //- glyph stays live while the link is disabled: an address in transit
            //- is still worth handing on.
            .bq-share-address
                a.bq-share-url(
                    :href="hostingUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    translate="no"
                    :class="{ disabled: !hostingLinkLive }"
                ) {{ hostingUrlLabel }}
                CopyText.bq-share-copy(:value="hostingUrl")
                    span.material-symbols-outlined.bq-share-copy-icon(translate="no" aria-hidden="true") content_copy
            .bq-share-actions
                button.bq-share-btn.bq-share-btn--ghost(
                    type="button"
                    @click="editSubdomain"
                    :class="{ disabled: !canEditSubdomain }"
                ) Change
                button.bq-share-btn.bq-share-btn--danger(
                    type="button"
                    @click="confirmRemoveSubdomain = true"
                    :class="{ disabled: !canEditSubdomain }"
                ) Remove
        small.bq-share-note(v-if="hostingLinkNote") {{ hostingLinkNote }}

    section#settings-hosting.bq-share.bq-share--empty(v-else)
        .bq-share-head
            span.bq-share-title Web Hosting
            small.bq-share-hint No hosting address yet. Register a subdomain to serve your site at <span translate="no">&lt;name&gt;.{{ hostDomain }}</span>. #[a.bq-cta-link(:href="PROJECT_DOCS.hosting" target="_blank" rel="noopener" style='white-space: nowrap;') Learn more]
        .bq-share-actions
            button.bq-share-btn(
                type="button"
                @click="editSubdomain"
                :class="{ disabled: !canEditSubdomain }"
            ) Register

    //- The sender address. Automated Emails and Newsletters refuse to open
    //- until one is registered, and they send the owner here to do it.
    section#settings-email-alias.bq-share(v-if="currentService.service.email_alias")
        .bq-share-head
            span.bq-share-title Email Alias
            small.bq-share-hint Automated emails and newsletters are sent from this address. #[a.bq-cta-link(:href="PROJECT_DOCS.emailAlias" target="_blank" rel="noopener" style='white-space: nowrap;') Learn more]
        .bq-share-bar
            //- Nothing to open, so the whole address copies, like a code block.
            CopyText.bq-share-address.bq-share-address--copy(:value="emailAliasLabel")
                span.bq-share-url {{ emailAliasLabel }}
                span.material-symbols-outlined.bq-share-copy-icon(translate="no" aria-hidden="true") content_copy
            .bq-share-actions
                button.bq-share-btn.bq-share-btn--ghost(
                    type="button"
                    @click="editEmailAlias"
                    :class="{ disabled: !canEditSettings || updatingValue.email_alias }"
                ) Change
                button.bq-share-btn.bq-share-btn--danger(
                    type="button"
                    @click="confirmRemoveEmailAlias = true"
                    :class="{ disabled: !canEditSettings || updatingValue.email_alias }"
                ) Remove

    section#settings-email-alias.bq-share.bq-share--empty(v-else)
        .bq-share-head
            span.bq-share-title Email Alias
            small.bq-share-hint Emails are sent from <span translate="no">{{ currentService.service.service }}@mail.skapi.com</span> until you register an alias. Automated Emails and Newsletters need one. #[a.bq-cta-link(:href="PROJECT_DOCS.emailAlias" target="_blank" rel="noopener" style='white-space: nowrap;') Learn more]
        .bq-share-actions
            button.bq-share-btn(
                type="button"
                @click="editEmailAlias"
                :class="{ disabled: !canEditSettings }"
            ) Register

    //- Advanced (expert tools)
    .bq-meta(:class="{ disabled: !user?.email_verified || currentService.service.active < 0 }")
        h2.bq-section-title Advanced

        .bq-row
            .bq-toggle-label
                span CORS
                small.bq-toggle-hint(translate="no") {{ currentService.service.cors || "*" }}

            a.bq-link(
                @click="editCors"
                :class="{ disabled: !user?.email_verified || currentService.service.active < 0 }"
            ) [Edit]

        .bq-toggle-row
            .bq-toggle-label
                span Freeze Database
                small.bq-toggle-hint When on, the database cannot be modified by non-admin users.
            Toggle(
                :active="currentService.service.freeze_database"
                :disabled="currentService.service.active < 0 || updatingValue.freeze_database"
                @click="changeFreezeDatabase(!currentService.service.freeze_database)"
            )

        .bq-toggle-row
            .bq-toggle-label
                span Allow Signup
                small.bq-toggle-hint When on, visitors can create their own account in this project.
            Toggle(
                :active="!currentService.service.prevent_signup"
                :disabled="currentService.service.active < 0 || updatingValue.prevent_signup"
                @click="changeCreateUserMode(!currentService.service.prevent_signup)"
            )

        //- prevent_anonymous is the skapi-level flag governing anonymous record
        //- WRITES. A plain setServiceOption toggle, like Prevent Inquiry.
        .bq-toggle-row
            .bq-toggle-label
                span Prevent Anonymous
                small.bq-toggle-hint When on, only signed-in users can write to the database.
            Toggle(
                :active="currentService.service.prevent_anonymous"
                :disabled="currentService.service.active < 0 || updatingValue.prevent_anonymous"
                @click="changePreventAnonymous(!currentService.service.prevent_anonymous)"
            )

        .bq-toggle-row
            .bq-toggle-label
                span Prevent Inquiry
                small.bq-toggle-hint When on, visitors cannot send inquiries to this project.
            Toggle(
                :active="currentService.service.prevent_inquiry"
                :disabled="currentService.service.active < 0 || updatingValue.prevent_inquiry"
                @click="changePreventInquiry(!currentService.service.prevent_inquiry)"
            )

        //- Stays usable at active === 0: that is the state it exists to undo.
        .bq-toggle-row
            .bq-toggle-label
                span Project Enabled
                small.bq-toggle-hint When on, users can access this project.
            Toggle(
                :disabled="currentService.service.active < 0 || updatingValue.enableDisable"
                :active="currentService.service.active >= 1"
                @click="enableDisable"
            )

        //- .bq-row.bq-api-key-row
        //-     .bq-toggle-label
        //-         span API Key
        //-         small.bq-toggle-hint.bq-api-key-value
        //-             span.bq-api-key-slot(v-if="currentService.service.api_key" translate="no") {{ currentService.service.api_key }}
        //-             span.bq-api-key-slot(v-else) Not set
        //-     a.bq-link(
        //-         @click="editApiKey"
        //-         :class="{ disabled: !user?.email_verified || currentService.service.active < 0 }"
        //-     ) [Edit]

    //- delete
    .bq-section(v-if="canDeleteService" style="text-align:right")
        router-link.bq-link.bq-danger(
            :to="'/delete-service/' + currentService.id"
            :class="{ disabled: !user?.email_verified || currentService.service.active == 0 }"
        ) [Delete Project]

    //- modals
    Modal(:open="modifyMode.name" @close="() => { if (!updatingValue.name) modifyMode.name = false }")
        .bq-modal-wrap
            .bq-modal-close(@click="modifyMode.name = false" :class="{ disabled: updatingValue.name }") &times;
            form.bq-modal-step(@submit.prevent="changeName")
                h2 Project Name
                p.bq-modal-desc Enter a new name for this project (max 256 characters).
                input.bq-modal-input(
                    type="text"
                    ref="focus_name"
                    placeholder="Max 256 characters"
                    maxlength="256"
                    :value="inputName"
                    @input="e => inputName = e.target.value"
                    :disabled="updatingValue.name"
                    required
                )
                button.btn(
                    type="submit"
                    :class="{ 'bq-btn--loading': updatingValue.name }"
                )
                    span.bq-loader(v-if="updatingValue.name")
                    template(v-else) Save

    Modal(:open="modifyMode.cors" @close="() => { if (!updatingValue.cors) modifyMode.cors = false }")
        .bq-modal-wrap
            .bq-modal-close(@click="modifyMode.cors = false" :class="{ disabled: updatingValue.cors }") &times;
            form.bq-modal-step(@submit.prevent="changeCors")
                h2 CORS
                p.bq-modal-desc When set, this service will only accept requests from the specified origins.
                p.bq-modal-desc Comma separated origins.#[br]Leave empty to allow every origin.
                input#modifyCors.bq-modal-input(
                    ref="focus_cors"
                    :disabled="updatingValue.cors || null"
                    type="text"
                    placeholder="https://your.domain1.com, https://your.domain2.com, ..."
                    :value="inputCors"
                    @input="e => { e.target.setCustomValidity(''); inputCors = e.target.value; }"
                )
                button.btn(
                    type="submit"
                    :class="{ 'bq-btn--loading': updatingValue.cors }"
                )
                    span.bq-loader(v-if="updatingValue.cors")
                    template(v-else) Save

    Modal(:open="modifyMode.description" @close="() => { if (!updatingValue.description) modifyMode.description = false }")
        .bq-modal-wrap
            .bq-modal-close(@click="modifyMode.description = false" :class="{ disabled: updatingValue.description }") &times;
            form.bq-modal-step(@submit.prevent="changeDescription")
                h2 Description
                p.bq-modal-desc Optional. Add a short description or base prompt for your project. This can be used to provide default context to AI.
                textarea.bq-modal-textarea(
                    ref="focus_description"
                    :disabled="updatingValue.description || null"
                    placeholder="Describe your project..."
                    maxlength="1000"
                    rows="4"
                    :value="inputDescription"
                    @input="e => inputDescription = e.target.value"
                )
                button.btn(
                    type="submit"
                    :class="{ 'bq-btn--loading': updatingValue.description }"
                )
                    span.bq-loader(v-if="updatingValue.description")
                    template(v-else) Save

    //- Modal(:open="modifyMode.api_key" @close="() => { if (!updatingValue.api_key) modifyMode.api_key = false }")
    //-     .bq-modal-wrap
    //-         .bq-modal-close(@click="modifyMode.api_key = false" :class="{ disabled: updatingValue.api_key }") &times;
    //-         form.bq-modal-step(@submit.prevent="changeApiKey")
    //-             h2 API Key
    //-             p.bq-modal-desc 4 to 256 characters. Used by the legacy per-project API key check.
    //-             input.bq-modal-input(
    //-                 type="text"
    //-                 ref="focus_key"
    //-                 minlength="4"
    //-                 maxlength="256"
    //-                 autocomplete="off"
    //-                 spellcheck="false"
    //-                 translate="no"
    //-                 :value="inputKey"
    //-                 @input="e => inputKey = e.target.value"
    //-                 :disabled="updatingValue.api_key"
    //-                 required
    //-             )
    //-             button.btn(
    //-                 type="submit"
    //-                 :class="{ 'bq-btn--loading': updatingValue.api_key }"
    //-             )
    //-                 span.bq-loader(v-if="updatingValue.api_key")
    //-                 template(v-else) Save

    Modal(:open="modifyMode.subdomain" @close="() => { if (!updatingValue.subdomain) modifyMode.subdomain = false }")
        .bq-modal-wrap
            .bq-modal-close(@click="modifyMode.subdomain = false" :class="{ disabled: updatingValue.subdomain }") &times;
            form.bq-modal-step(@submit.prevent="changeSubdomain")
                h2 {{ hostingSubdomain ? 'Change Web Hosting Address' : 'Web Hosting Address' }}
                p.bq-modal-desc Lowercase letters, digits and hyphens, {{ SUBDOMAIN_MIN }} to {{ NAME_MAX }} characters.#[br]This becomes <span translate="no">https://&lt;my-site&gt;.skapi.com</span>.
                //- The fixed part of the address sits inside the field, so the
                //- owner sees what the name turns into while typing it.
                label.bq-modal-affix(:class="{ disabled: updatingValue.subdomain }")
                    input.bq-modal-input(
                        type="text"
                        ref="focus_subdomain"
                        placeholder="my-site"
                        :pattern="NAME_PATTERN_ATTR"
                        :minlength="SUBDOMAIN_MIN"
                        :maxlength="NAME_MAX"
                        autocomplete="off"
                        spellcheck="false"
                        translate="no"
                        :value="inputSubdomain"
                        @input="e => onSubdomainInput(e.target.value)"
                        :disabled="updatingValue.subdomain"
                        required
                    )
                    span.bq-modal-suffix(translate="no") .{{ hostDomain }}
                small.bq-share-status(:class="subdomainCheck.status") {{ subdomainCheck.message }}
                button.btn(
                    type="submit"
                    :disabled="subdomainCheck.status !== 'available' && !updatingValue.subdomain"
                    :class="{ 'bq-btn--loading': updatingValue.subdomain }"
                )
                    span.bq-loader(v-if="updatingValue.subdomain")
                    template(v-else) {{ hostingSubdomain ? 'Change' : 'Register' }}

    Modal(:open="confirmRemoveSubdomain" persistent @close="() => { if (!updatingValue.subdomain) confirmRemoveSubdomain = false }")
        .bq-modal-delete
            .bq-modal-delete-header
                span.material-symbols-outlined.bq-modal-delete-icon(translate="no") delete
                span Remove Hosting Address
            .bq-modal-delete-body
                p Your site stops being served at this address. Files stay in storage.
                p.bq-modal-delete-warn(translate="no") {{ hostingUrlLabel }}
            .bq-modal-delete-footer
                template(v-if="updatingValue.subdomain")
                    span.bq-loader.bq-loader--right Removing
                template(v-else)
                    button.bq-modal-delete-btn.bq-modal-delete-btn--cancel(type="button" @click="confirmRemoveSubdomain = false") Cancel
                    button.bq-modal-delete-btn.bq-modal-delete-btn--delete(type="button" @click="removeSubdomain") Remove

    Modal(:open="modifyMode.email_alias" @close="() => { if (!updatingValue.email_alias) modifyMode.email_alias = false }")
        .bq-modal-wrap
            .bq-modal-close(@click="modifyMode.email_alias = false" :class="{ disabled: updatingValue.email_alias }") &times;
            form.bq-modal-step(@submit.prevent="changeEmailAlias")
                h2 Email Alias
                p.bq-modal-desc Lowercase letters, digits and hyphens, {{ ALIAS_MIN }} to {{ NAME_MAX }} characters.#[br]This becomes <span translate="no">&lt;alias&gt;@mail.skapi.com</span>.
                label.bq-modal-affix(:class="{ disabled: updatingValue.email_alias }")
                    input.bq-modal-input(
                        type="text"
                        ref="focus_email_alias"
                        placeholder="alias"
                        :pattern="NAME_PATTERN_ATTR"
                        :minlength="ALIAS_MIN"
                        :maxlength="NAME_MAX"
                        autocomplete="off"
                        spellcheck="false"
                        translate="no"
                        :value="inputEmailAlias"
                        @input="e => onEmailAliasInput(e.target.value)"
                        :disabled="updatingValue.email_alias"
                        required
                    )
                    span.bq-modal-suffix(translate="no") @{{ MAIL_DOMAIN }}
                small.bq-share-status(:class="emailAliasCheck.status") {{ emailAliasCheck.message }}
                button.btn(
                    type="submit"
                    :disabled="emailAliasCheck.status !== 'available' && !updatingValue.email_alias"
                    :class="{ 'bq-btn--loading': updatingValue.email_alias }"
                )
                    span.bq-loader(v-if="updatingValue.email_alias")
                    template(v-else) {{ currentService.service.email_alias ? 'Change' : 'Register' }}

    Modal(:open="confirmRemoveEmailAlias" persistent @close="() => { if (!updatingValue.email_alias) confirmRemoveEmailAlias = false }")
        .bq-modal-delete
            .bq-modal-delete-header
                span.material-symbols-outlined.bq-modal-delete-icon(translate="no") delete
                span Remove Email Alias
            .bq-modal-delete-body
                p Emails are sent from <span translate="no">{{ currentService.service.service }}@{{ MAIL_DOMAIN }}</span> again. Automated Emails and Newsletters stay closed until you register a new alias.
                p.bq-modal-delete-warn(translate="no") {{ emailAliasLabel }}
            .bq-modal-delete-footer
                template(v-if="updatingValue.email_alias")
                    span.bq-loader.bq-loader--right Removing
                template(v-else)
                    button.bq-modal-delete-btn.bq-modal-delete-btn--cancel(type="button" @click="confirmRemoveEmailAlias = false") Cancel
                    button.bq-modal-delete-btn.bq-modal-delete-btn--delete(type="button" @click="removeEmailAlias") Remove
</template>

<script setup lang="ts">
import TemplateLinks from "@/components/templateLinks.vue";
import AgentPrompts from "@/components/agentPrompts.vue";
import { useRoute, useRouter } from "vue-router";
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { currentService } from "@/views/project-list";
import { SkapiError } from "skapi-js";
import { dateFormat } from "@/code/utils";
import { user } from "@/code/user";
import Modal from "@/components/modal.vue";
import Toggle from "@/components/toggle.vue";
import CopyText from "@/components/copyText.vue";
import { showToast } from "@/components/toast";
import { MAIL_DOMAIN } from "@/views/service/mail/mail";
import { removeFullScreenLoader } from "@/components/jsComponents";
import { hostDomain, registeredSubdomain, subdomainPending } from "@/code/service";
import { isServiceContained } from "@/code/service-spec";
import { STORED_LIMIT_LABEL } from "@/code/plan-state";
import { PLAN_DOCS, PROJECT_DOCS, bandwidthMeterNote, emailSendsMeterNote, emailStorageMeterNote, hardStopNote, planCtaLabel, suspendedLimitRows } from "@/code/plan-copy";
import { regionLabel } from "@/code/regions";

const router = useRouter();
const route = useRoute();

// The region is picked by city when a project is created, so name it here too
// rather than leaving the reader to decode "eu-west-3". Falls back to the bare
// code for a region the catalogue does not know.
const regionText = (region: string): string => {
    let label = regionLabel(region);
    return label && label !== region ? `${region} (${label})` : region || "";
};

let inputName = "";
let inputCors = "";
let inputDescription = "";
let inputKey = "";
let modifyMode = reactive({
    name: false,
    cors: false,
    description: false,
    api_key: false,
    subdomain: false,
    email_alias: false,
});

let updatingValue = reactive({
    name: false,
    cors: false,
    description: false,
    api_key: false,
    subdomain: false,
    email_alias: false,
    prevent_signup: false,
    prevent_anonymous: false,
    prevent_inquiry: false,
    freeze_database: false,
    enableDisable: false,
});

// registerSubdomain and registerSenderEmail throw plain STRINGS for their local
// checks, so a message has to be read off either shape.
let errText = (err: any) =>
    (typeof err === "string" ? err : err?.message) || "Something went wrong.";

// The page-wide rule every [Edit] link and card button shares.
let canEditSettings = computed(
    () => !!user?.email_verified && currentService.value.service.active >= 0,
);

/** A suspended project's over-limit rows, for the Plan & Usage card. */
const suspendRows = computed(() => suspendedLimitRows(currentService.value));

/** The one line under the Email sends and Email Storage meters. */
const sendsNote = computed(() => emailSendsMeterNote(currentService.value));
const storageNote = computed(() => emailStorageMeterNote(currentService.value));

let focus_name = ref();
let focus_cors = ref();
let focus_description = ref();
let focus_key = ref();
let focus_subdomain = ref();
let focus_email_alias = ref();

/* ─── web hosting (the card at the top of the page) ─── */
// A subdomain carries a one-character status flag while it is in transit
// ('+new'), being released ('*old') or held ('!old'). registeredSubdomain
// answers the bare name, the `h1-<md5>` one the backend gives every new service
// included, so the card reads "registered" the same way the project list and
// the hosting gate do.
let hostingSubdomain = computed(() => registeredSubdomain(currentService.value));

let hostingUrlLabel = computed(() =>
    hostingSubdomain.value ? `${hostingSubdomain.value}.${hostDomain}` : "",
);

let hostingUrl = computed(() =>
    hostingUrlLabel.value ? `https://${hostingUrlLabel.value}` : "",
);

// Whether the address is serving right now. A flagged subdomain still has a
// readable address worth copying, but opening it would land on nothing yet.
let hostingLinkLive = computed(
    () => !!hostingSubdomain.value && !subdomainPending(currentService.value),
);

let hostingLinkNote = computed(() => {
    const sd = currentService.value?.service?.subdomain || "";
    if (!sd) return "";
    if (sd[0] === "+") return "This address is still being set up. It goes live in a few minutes.";
    if (sd[0] === "*") return "This address is being released and will stop working.";
    if (sd[0] === "!") return "This address is on hold.";
    return "";
});

// registerSubdomain refuses a new name while the old one is still in transit
// ('+') or being released ('*'), so Change waits those two out. An address on
// hold ('!') can still be changed or removed.
let hostingLocked = computed(() => {
    const sd = currentService.value?.service?.subdomain || "";
    return sd[0] === "+" || sd[0] === "*";
});

let canEditSubdomain = computed(
    () => canEditSettings.value && !hostingLocked.value && !updatingValue.subdomain,
);

/* ─── email alias (the card under it) ─── */
let emailAliasLabel = computed(() => {
    const alias = currentService.value?.service?.email_alias || "";
    return alias ? `${alias}@${MAIL_DOMAIN}` : "";
});

/* ─── name rules shared by the subdomain and the alias ─── */
// A DNS label, up to 32 characters: at least 5 for a subdomain (register-subdomain
// refuses shorter), 6 for an email alias. The regex source doubles as the input's
// `pattern` so the two can never disagree. Reserved names are the backend's call: the
// availability check refuses them with "Subdomain is reserved.", shown as its own message.
type CheckStatus = "idle" | "checking" | "invalid" | "available" | "unavailable" | "error";

const NAME_PATTERN = /^[a-z\d](?:[a-z\d\-]{0,61}[a-z\d])?$/;
const NAME_PATTERN_ATTR = NAME_PATTERN.source;
const SUBDOMAIN_MIN = 5;
const ALIAS_MIN = 6;
const NAME_MAX = 32;

const validateName = (value: string, min: number): string | null => {
    if (!value) return null;
    if (value.length < min) return `Please enter at least ${min} characters.`;
    if (value.length > NAME_MAX) return `Maximum ${NAME_MAX} characters are allowed.`;
    if (!NAME_PATTERN.test(value))
        return "Only lowercase letters, numbers, and hyphens (-) are allowed, and hyphens cannot be at the start or end.";
    if (value.startsWith("-") || value.endsWith("-"))
        return "The first and last characters cannot be hyphens.";
    if (/--/.test(value)) return "Hyphens cannot be used consecutively.";
    return null;
};

/* ─── subdomain modal ─── */
let inputSubdomain = ref("");
let subdomainCheck = reactive<{ status: CheckStatus; message: string }>({
    status: "idle",
    message: "",
});
let subdomainCheckTimer: ReturnType<typeof setTimeout> | null = null;
// Bumped on every keystroke and on every check, so a slow answer to an earlier
// value can never overwrite the verdict on the current one.
let subdomainCheckToken = 0;
let confirmRemoveSubdomain = ref(false);

const resetSubdomainCheck = () => {
    if (subdomainCheckTimer) {
        clearTimeout(subdomainCheckTimer);
        subdomainCheckTimer = null;
    }
    subdomainCheckToken++;
    subdomainCheck.status = "idle";
    subdomainCheck.message = "";
};

const runSubdomainCheck = async (value: string) => {
    const token = ++subdomainCheckToken;
    const problem = validateName(value, SUBDOMAIN_MIN);
    if (problem) {
        subdomainCheck.status = "invalid";
        subdomainCheck.message = problem;
        return;
    }

    subdomainCheck.status = "checking";
    subdomainCheck.message = "Checking availability...";
    try {
        const res: any = await currentService.value.registerSubdomain({ subdomain: value, check: true });
        if (token !== subdomainCheckToken) return;
        // The check answers { status: 'success', resp } where resp is a string
        // (or { body: string }) that contains "SUCCESS" when the name is free.
        const body = res?.resp;
        const text =
            typeof body === "string" ? body
            : typeof body?.body === "string" ? body.body
            : JSON.stringify(body ?? "");
        if (res?.status === "success" && text.includes("SUCCESS")) {
            subdomainCheck.status = "available";
            subdomainCheck.message = "This address is available.";
        } else {
            subdomainCheck.status = "unavailable";
            subdomainCheck.message = "This address is already in use.";
        }
    } catch (err) {
        if (token !== subdomainCheckToken) return;
        const msg = errText(err);
        if (/taken|exist|already/i.test(msg)) {
            subdomainCheck.status = "unavailable";
            subdomainCheck.message = "This address is already in use.";
        } else if (/reserved/i.test(msg)) {
            // official sounding and brand names (checkout, skapi-support, paypal-login)
            subdomainCheck.status = "unavailable";
            subdomainCheck.message = "This address is reserved. Please choose another name.";
        } else {
            subdomainCheck.status = "error";
            subdomainCheck.message = msg;
        }
    }
};

let onSubdomainInput = (raw: string) => {
    inputSubdomain.value = raw;
    resetSubdomainCheck();
    const value = raw.trim();
    if (!value) return;
    // The name already registered is neither free nor taken; there is simply
    // nothing to change.
    if (value === hostingSubdomain.value) {
        subdomainCheck.message = "This is your current address.";
        return;
    }
    subdomainCheckTimer = setTimeout(() => {
        subdomainCheckTimer = null;
        runSubdomainCheck(value);
    }, 500);
};

let editSubdomain = () => {
    if (!canEditSubdomain.value) return;
    inputSubdomain.value = hostingSubdomain.value;
    resetSubdomainCheck();
    if (hostingSubdomain.value) subdomainCheck.message = "This is your current address.";
    modifyMode.subdomain = true;
    nextTick(() => {
        focus_subdomain.value?.focus();
    });
};

let changeSubdomain = async () => {
    if (subdomainCheck.status !== "available" || updatingValue.subdomain) return;
    const value = inputSubdomain.value.trim();
    updatingValue.subdomain = true;
    try {
        // registerSubdomain replaces this.service with the server's answer (the
        // new name carries its '+' transit flag) and starts getSubdomainInfo's
        // polling, so the card and its note follow on their own.
        await currentService.value.registerSubdomain({ subdomain: value });
        modifyMode.subdomain = false;
    } catch (err) {
        showToast(errText(err));
    } finally {
        updatingValue.subdomain = false;
    }
};

// No argument = removal. The old name comes back flagged '*' until the
// release completes, which the card reports as "being released".
let removeSubdomain = async () => {
    if (updatingValue.subdomain) return;
    updatingValue.subdomain = true;
    try {
        await currentService.value.registerSubdomain();
        confirmRemoveSubdomain.value = false;
        modifyMode.subdomain = false;
    } catch (err) {
        showToast(errText(err));
    } finally {
        updatingValue.subdomain = false;
    }
};

/* ─── email alias modal ─── */
let inputEmailAlias = ref("");
let emailAliasCheck = reactive<{ status: CheckStatus; message: string }>({
    status: "idle",
    message: "",
});
let emailAliasCheckTimer: ReturnType<typeof setTimeout> | null = null;
let emailAliasCheckToken = 0;
let confirmRemoveEmailAlias = ref(false);

const resetEmailAliasCheck = () => {
    if (emailAliasCheckTimer) {
        clearTimeout(emailAliasCheckTimer);
        emailAliasCheckTimer = null;
    }
    emailAliasCheckToken++;
    emailAliasCheck.status = "idle";
    emailAliasCheck.message = "";
};

const runEmailAliasCheck = async (value: string) => {
    const token = ++emailAliasCheckToken;
    const problem = validateName(value, ALIAS_MIN);
    if (problem) {
        emailAliasCheck.status = "invalid";
        emailAliasCheck.message = problem;
        return;
    }

    emailAliasCheck.status = "checking";
    emailAliasCheck.message = "Checking availability...";
    try {
        // Answers a string that contains "SUCCESS" when the alias is free; the
        // backend rejects a taken one, so any throw reads as "in use".
        const res = await currentService.value.registerSenderEmail({ email_alias: value, check: true });
        if (token !== emailAliasCheckToken) return;
        if (String(res ?? "").includes("SUCCESS")) {
            emailAliasCheck.status = "available";
            emailAliasCheck.message = "This email alias is available.";
        } else {
            emailAliasCheck.status = "unavailable";
            emailAliasCheck.message = "This email alias is already in use.";
        }
    } catch {
        if (token !== emailAliasCheckToken) return;
        emailAliasCheck.status = "unavailable";
        emailAliasCheck.message = "This email alias is already in use.";
    }
};

let onEmailAliasInput = (raw: string) => {
    inputEmailAlias.value = raw;
    resetEmailAliasCheck();
    const value = raw.trim();
    if (!value) return;
    if (value === (currentService.value?.service?.email_alias || "")) {
        emailAliasCheck.message = "This is your current alias.";
        return;
    }
    emailAliasCheckTimer = setTimeout(() => {
        emailAliasCheckTimer = null;
        runEmailAliasCheck(value);
    }, 500);
};

let editEmailAlias = () => {
    if (!canEditSettings.value) return;
    const current = currentService.value?.service?.email_alias || "";
    inputEmailAlias.value = current;
    resetEmailAliasCheck();
    if (current) emailAliasCheck.message = "This is your current alias.";
    modifyMode.email_alias = true;
    nextTick(() => {
        focus_email_alias.value?.focus();
    });
};

let changeEmailAlias = async () => {
    if (emailAliasCheck.status !== "available" || updatingValue.email_alias) return;
    const value = inputEmailAlias.value.trim();
    updatingValue.email_alias = true;
    try {
        // Writes service.email_alias on success, so the card flips by itself.
        await currentService.value.registerSenderEmail({ email_alias: value });
        modifyMode.email_alias = false;
    } catch (err) {
        showToast(errText(err));
    } finally {
        updatingValue.email_alias = false;
    }
};

// No argument = removal. Clears service.email_alias, so the card drops back to
// its Register state and the gated pages close again.
let removeEmailAlias = async () => {
    if (updatingValue.email_alias) return;
    updatingValue.email_alias = true;
    try {
        await currentService.value.registerSenderEmail();
        confirmRemoveEmailAlias.value = false;
    } catch (err) {
        showToast(errText(err));
    } finally {
        updatingValue.email_alias = false;
    }
};

/* ─── focus target (?focus=hosting | email) ─── */
// The gated pages (Web Hosting, Automated Emails, Newsletters) send an owner
// here with the card they need named in the query. Scroll it into view, pulse
// it once, then drop the query so a reload does not replay the pulse.
const FOCUS_TARGETS: Record<string, string> = {
    hosting: "settings-hosting",
    email: "settings-email-alias",
};
let focusPulseTimer: ReturnType<typeof setTimeout> | null = null;

// Dropping the query keeps a reload from replaying the pulse. It changes the
// query only, which the router now leaves the scroll position alone for.
const clearFocusQuery = () => {
    const query = { ...route.query };
    delete query.focus;
    router.replace({ query });
};

const applyFocus = () => {
    const key = route.query.focus;
    const id = typeof key === "string" ? FOCUS_TARGETS[key] : "";
    if (!id) return;

    // On a frame rather than a tick: the router puts a newly opened page at the
    // top of its own accord, and it does that after this component has mounted,
    // so scrolling during the mount was simply undone. A frame later this runs
    // last. The card can also be a render or two behind the mount, so look for
    // it over a few frames before giving up on it.
    let tries = 0;
    const focusCard = () => {
        const el = document.getElementById(id);
        if (!el) {
            if (tries++ < 30) requestAnimationFrame(focusCard);
            else clearFocusQuery();
            return;
        }
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("bq-focus-pulse");
        if (focusPulseTimer) clearTimeout(focusPulseTimer);
        focusPulseTimer = setTimeout(() => {
            el.classList.remove("bq-focus-pulse");
            focusPulseTimer = null;
        }, 2200);
        clearFocusQuery();
    };
    requestAnimationFrame(focusCard);
};

watch(() => route.query.focus, () => applyFocus());

onMounted(() => {
    removeFullScreenLoader();
    // The transit note reads the flag off the subdomain itself, but the settled
    // state lives in subdInfo, which nothing else on this page fetches. Ask once
    // when a registered address has never been looked up (srvc is set by the
    // first answer, so a second mount does not ask again).
    const svc = currentService.value;
    if (svc && hostingSubdomain.value && !svc.subdInfo.stat && !svc.subdInfo.srvc) {
        Promise.resolve(svc.getSubdomainInfo()).catch(() => {});
    }
    applyFocus();
});

onUnmounted(() => {
    if (subdomainCheckTimer) clearTimeout(subdomainCheckTimer);
    if (emailAliasCheckTimer) clearTimeout(emailAliasCheckTimer);
    if (focusPulseTimer) clearTimeout(focusPulseTimer);
});

let canDeleteService = computed(() => {
    return (
        (currentService.value.service.active < 0 &&
            !isServiceContained(currentService.value.service.active)) ||
        currentService.value.formattedSpecs.currentPlan.label === "Free" ||
        currentService.value.formattedSpecs.currentPlan.label === "Unlimited" ||
        currentService.value.subscription?.will_cancel
    );
});

let editName = () => {
    inputName = currentService.value.service.name;
    modifyMode.name = true;
    nextTick(() => {
        focus_name.value.focus();
    });
};
let changeName = () => {
    if (currentService.value.service.name !== inputName) {
        let regex = /[<>`]/g;
        if (inputName.match(regex)) {
            showToast("Angle brackets and backticks are not allowed", "warning");
            return;
        }

        let previous = currentService.value.service.name;
        updatingValue.name = true;

        currentService.value
            .setServiceOption({ name: inputName })
            .then(() => {
                updatingValue.name = false;
                currentService.value.service.name = inputName;
                modifyMode.name = false;
            })
            .catch((err) => {
                if (!(err instanceof SkapiError)) throw err;
                updatingValue.name = false;
                currentService.value.service.name = previous;
                showToast(err.message);
            });
    } else {
        updatingValue.name = false;
        currentService.value.service.name = inputName;
        modifyMode.name = false;
    }
};

let editCors = () => {
    inputCors =
        currentService.value.service.cors === "*"
            ? ""
            : currentService.value.service.cors;
    modifyMode.cors = true;
    nextTick(() => {
        focus_cors.value.focus();
    });
};
let changeCors = () => {
    updatingValue.cors = true;
    currentService.value
        .setServiceOption({ cors: inputCors })
        .then(() => {
            updatingValue.cors = false;
            modifyMode.cors = false;
        })
        .catch((err) => {
            if (!(err instanceof SkapiError)) throw err;
            updatingValue.cors = false;
            nextTick(() => {
                let modifyCorsEl = document.getElementById("modifyCors") as HTMLInputElement;
                if (modifyCorsEl) {
                    modifyCorsEl.focus();
                    modifyCorsEl.setCustomValidity(err.message);
                    modifyCorsEl.reportValidity();
                }
            });
        });
};

let editDescription = () => {
    inputDescription = currentService.value.service.description || "";
    modifyMode.description = true;
    nextTick(() => {
        focus_description.value?.focus();
    });
};
let changeDescription = () => {
    updatingValue.description = true;
    let desc = inputDescription.trim();
    currentService.value
        .setServiceOption({ description: desc })
        .then(() => {
            updatingValue.description = false;
            currentService.value.service.description = desc;
            modifyMode.description = false;
        })
        .catch((err) => {
            if (!(err instanceof SkapiError)) throw err;
            updatingValue.description = false;
            showToast(err.message);
        });
};

// The deprecated per-service api_key. "service-settings" does not accept it, so
// it goes through Service.updateApiKey (the legacy register-service update).
let editApiKey = () => {
    inputKey = currentService.value.service.api_key || "";
    modifyMode.api_key = true;
    nextTick(() => {
        focus_key.value?.focus();
    });
};
let changeApiKey = () => {
    let previous = currentService.value.service.api_key;
    updatingValue.api_key = true;
    currentService.value
        .updateApiKey(inputKey)
        .then(() => {
            currentService.value.service.api_key = inputKey;
            modifyMode.api_key = false;
        })
        .catch((err) => {
            if (!(err instanceof SkapiError)) throw err;
            currentService.value.service.api_key = previous;
            showToast(err.message);
        })
        .finally(() => {
            updatingValue.api_key = false;
        });
};

// Toggle handlers. setServiceOption writes the new value onto this.service
// itself on success, so none of these has to write it back; a SkapiError is
// surfaced as a toast and anything else is rethrown.
let changeCreateUserMode = (onlyAdmin: boolean) => {
    updatingValue.prevent_signup = true;
    currentService.value
        .setServiceOption({ prevent_signup: onlyAdmin })
        .catch((err) => {
            if (!(err instanceof SkapiError)) throw err;
            showToast(err.message);
        })
        .finally(() => {
            updatingValue.prevent_signup = false;
        });
};

let enableDisable = async () => {
    updatingValue.enableDisable = true;
    try {
        if (currentService.value.service.active >= 1)
            await currentService.value.disableService();
        else await currentService.value.enableService();
    } catch (err) {
        if (!(err instanceof SkapiError)) throw err;
        showToast(err.message);
    } finally {
        updatingValue.enableDisable = false;
    }
};

let changePreventInquiry = (prevent: boolean) => {
    updatingValue.prevent_inquiry = true;
    currentService.value
        .setServiceOption({ prevent_inquiry: prevent })
        .catch((err) => {
            if (!(err instanceof SkapiError)) throw err;
            showToast(err.message);
        })
        .finally(() => {
            updatingValue.prevent_inquiry = false;
        });
};

let changePreventAnonymous = (prevent: boolean) => {
    updatingValue.prevent_anonymous = true;
    currentService.value
        .setServiceOption({ prevent_anonymous: prevent })
        .catch((err) => {
            if (!(err instanceof SkapiError)) throw err;
            showToast(err.message);
        })
        .finally(() => {
            updatingValue.prevent_anonymous = false;
        });
};

let changeFreezeDatabase = (freeze: boolean) => {
    updatingValue.freeze_database = true;
    currentService.value
        .setServiceOption({ freeze_database: freeze })
        .catch((err) => {
            if (!(err instanceof SkapiError)) throw err;
            showToast(err.message);
        })
        .finally(() => {
            updatingValue.freeze_database = false;
        });
};

const barColor = (percent: string) => {
    const n = Number(percent.toString().replace("%", ""));
    if (!n) return "";
    // Past 100 on a plan that bills instead of stopping is not a danger, it
    // is a charge: colour it as information, not as a warning.
    if (n > 100 && currentService.value?.formattedSpecs?.currentPlan?.hard_stop === false) return "bq-bar--over";
    if (n >= 80) return "bq-bar--danger";
    if (n >= 50) return "bq-bar--warn";
    return "";
};

// The stacked file pool bar keeps its segment colours whatever the level; the
// TRACK carries the state instead: a warning ring near the ceiling, a danger
// ring on a hard stop plan that has hit it, an informational ring on an overage
// plan past it.
const poolTrackClass = (percent: string) => {
    const n = Number(percent.toString().replace("%", ""));
    if (!n) return "";
    if (n > 100 && currentService.value?.formattedSpecs?.currentPlan?.hard_stop === false) return "bq-bar--over";
    if (n >= 100) return "bq-bar--danger";
    if (n >= 80) return "bq-bar--warn";
    return "";
};

// Usage can exceed the limit on a plan without a hard stop, and a fill wider
// than its track paints over the layout. The number beside the bar still says
// how far over it is.
const barWidth = (percent: string) => {
    const n = Number(percent.toString().replace("%", ""));
    if (!Number.isFinite(n) || n <= 0) return "0%";
    return `${Math.min(n, 100)}%`;
};
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

.bq-dashboard {
    font-family: @mono;
    color: @ink;
}

.bq-dash-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
    gap: 1rem;
}

.bq-dash-title {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.4rem;
    min-width: 0;
}

// .bq-title {
//     font-size: 1.1rem;
//     font-weight: 600;
//     margin: 0.35rem 0;
//     letter-spacing: -0.01em;
//     word-break: break-word;
// }

/* --- address cards (top of page) --- */
// Carries the same extruded border+shadow as .bq-meta so it reads as part of
// the page.
.bq-share {
    margin-bottom: 1.5rem;
    border: 1px solid @line;
    padding: 1rem;
    box-shadow: 2px 2px var(--bq-shadow);
}

.bq-share-head {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    margin-bottom: 0.85rem;
}

.bq-share-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: @ink;
}

.bq-share-hint {
    font-size: 0.75rem;
    color: @muted;
    line-height: 1.4;
}

.bq-share-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
    border: 1px solid @line;
    background: @paper;
    padding: 0.55rem 0.5rem 0.55rem 0.75rem;
}

// The address itself. It is the whole point of the card, so it gets the largest
// type on it and, on a narrow bar, the whole first line.
.bq-share-url {
    min-width: 0;
    font-family: @mono;
    font-size: 0.95rem;
    color: @pink;
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
        text-decoration: underline;
    }

    &.disabled {
        color: @muted;
        pointer-events: none;
        text-decoration: none;
    }
}

// The address and its copy glyph, held together so the glyph never wraps away
// from the text it copies. Prefixed with .bq-share-bar because CopyText's own
// scoped `.copy-text { display: inline }` and hover underline tie a single class.
.bq-share-bar .bq-share-address {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    min-width: 0;
}

// Code-block copy language (landing.vue .bq-code-copy): the glyph is always
// visible, muted, and turns brand blue under the pointer; nothing underlines.
.bq-share-copy-icon {
    flex-shrink: 0;
    font-size: 1.05rem;
    line-height: 1;
    color: @muted;
    transition: color 0.12s ease;
}

.bq-share-bar .bq-share-copy,
.bq-share-bar .bq-share-address--copy {
    display: inline-flex;

    &:hover {
        text-decoration: none;

        .bq-share-copy-icon {
            color: @pink;
        }
    }
}

// The whole e-mail address is the copy target, so its text drops the link underline.
.bq-share-address--copy .bq-share-url:hover {
    text-decoration: none;
}

.bq-share-actions {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-shrink: 0;
}

// One rule for both the <button> and the <a>: base.less gives every <button>
// the extruded shadow and the hover/press motion, but an anchor gets none of
// it, so the pair would sit at different heights and move differently. Spelling
// both out here keeps them identical whichever element they are.
.bq-share-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.4rem 0.7rem;
    border: 1px solid @btn-bg;
    background: @btn-bg;
    color: @paper;
    font-family: @mono;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    line-height: 1.2;
    text-decoration: none;
    cursor: pointer;
    white-space: nowrap;
    box-shadow: 2px 2px var(--bq-shadow);
    transition: transform 0.12s ease, box-shadow 0.12s ease;

    &:hover {
        text-decoration: none;
    }

    // Only where hover is a real pointer state; a touch screen emulates it on
    // tap and would leave the button stuck raised (same guard as base.less).
    @media (hover: hover) and (pointer: fine) {
        &:hover {
            transform: translate(-1px, -1px);
            box-shadow: 3px 3px var(--bq-shadow);
        }
    }

    &:active {
        transform: translate(2px, 2px);
        box-shadow: none;
        transition-duration: 0.04s;
    }

    // Same as .bq-btn--secondary: a box button keeps its colours on hover and
    // answers with the lift/press motion only.
    &--ghost {
        border-color: @ink;
        background: @paper;
        color: @ink;
    }

    // Same as .bq-btn--danger, and red stays red on hover.
    &--danger {
        border-color: @danger;
        background: @danger;
        color: @paper;
    }

    &.disabled {
        opacity: 0.45;
        pointer-events: none;
    }
}

.bq-share-note {
    display: block;
    margin-top: 0.6rem;
    font-size: 0.72rem;
    color: @muted;
}

.bq-share--empty {
    border-color: @line;
    background: @paper;

    // The empty state carries its Register button under the head, so the head
    // keeps its gap; only a head with nothing after it loses it.
    .bq-share-head:last-child {
        margin-bottom: 0;
    }
}

// The availability line under a modal input (subdomain, e-mail alias).
.bq-share-status {
    display: block;
    min-height: 1.2em;
    margin: -0.25rem 0 1rem;
    font-size: 0.72rem;
    line-height: 1.4;
    color: @muted;

    &.available {
        color: @success;
    }

    &.invalid,
    &.unavailable,
    &.error {
        color: @danger;
    }
}

// A field with a fixed suffix (.skapi.com, @mail.skapi.com). The border moves
// from the input to the wrapper so the suffix reads as part of the field.
.bq-modal-affix {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
    border: 1px solid @line;
    background: @paper;
    cursor: text;
    transition: border-color 0.15s;

    &:focus-within {
        border-color: @ink;
    }

    &.disabled {
        opacity: 0.5;
    }

    // margin-top: base.less gives every `form label input` 8px, which pushed the
    // text below the suffix
    .bq-modal-input {
        flex: 1;
        min-width: 0;
        margin-top: 0;
        margin-bottom: 0;
        border: 0;
        padding-right: 0;
    }
}

.bq-modal-suffix {
    flex-shrink: 0;
    padding-right: 1rem;
    font-family: @mono;
    font-size: 0.9rem;
    color: @muted;
    white-space: nowrap;
    user-select: none;
}

@media (max-width: 480px) {
    .bq-share-actions {
        width: 100%;
    }

    .bq-share-btn {
        flex: 1;
        justify-content: center;
    }
}

/* --- sections --- */
.bq-section {
    margin-bottom: 2.5rem;

    &.disabled {
        opacity: 0.5;
        pointer-events: none;
    }
}

.bq-section-title {
    font-size: 1.1rem;
    font-weight: 700;
    margin: 0 0 1.25rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid @line;
}

/* --- rows --- */
.bq-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.85rem 0;
    border-bottom: 1px solid @line;
    font-size: 0.85rem;

    &:first-of-type {
        padding-top: 0;
    }

    &:last-of-type {
        border-bottom: none;
        padding-bottom: 0;
    }
}

.bq-row-label {
    min-width: 7rem;
    font-weight: 600;
    font-size: 0.78rem;
    letter-spacing: 0.04em;
    color: @muted;
    text-transform: uppercase;
    flex-shrink: 0;
}

.bq-row-value {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.bq-mono {
    font-family: @mono;
    font-size: 0.82rem;
}

/* --- plan header --- */
.bq-plan-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.bq-renew {
    font-size: 0.78rem;
    color: @muted;
}

/* --- meta --- */
.bq-meta {
    margin-bottom: 1.5rem;
    border: 1px solid @line;
    padding: 1rem;
    box-shadow: 2px 2px var(--bq-shadow);
}

.bq-meta-row {
    display: flex;
    justify-content: space-between;
    padding: 0.35rem 0;
    font-size: 0.78rem;

    span:first-child {
        color: @muted;
        min-width: 5rem;
    }

    span:last-child {
        text-align: right;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

// A copyable value in a meta row (the project id): the value and its glyph
// held together, the value keeping the row's ellipsis. Prefixed with
// .bq-meta-row to sit above CopyText's own inline + hover-underline rules and
// above the row's muted first-span rule.
.bq-meta-row .bq-meta-copy {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.35rem;
    min-width: 0;
    max-width: 100%;

    &:hover {
        text-decoration: none;

        .bq-share-copy-icon {
            color: @pink;
        }
    }

    .bq-meta-copy-value {
        color: @ink;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

// The glyph after a documentation link in a meta row. An atomic inline with its
// spacing in a margin, so the link's hover underline stops before it (see
// .bq-arrow in bq-shared.less; never a flex container). Three classes on
// purpose: the glyph is both the first and the last span of its link, so the
// row's own `span:first-child` (muted, min-width: 5rem, the label column) and
// `span:last-child` (clipped, right aligned), both (0,2,1), would land on it.
.bq-meta-row .bq-cta-link .bq-meta-doc-icon {
    display: inline-block;
    min-width: 0;
    margin-left: 0.3em;
    font-size: 0.95rem;
    line-height: 1;
    vertical-align: -0.18em;
    color: inherit;
    overflow: visible;
    text-decoration: none;
}

/* --- usage bars --- */
.bq-usage-bars {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.bq-usage-item {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}

.bq-usage-head {
    display: flex;
    justify-content: space-between;
    font-size: 0.78rem;
    color: @muted;
}

.bq-bar {
    height: 6px;
    background: @line;
    overflow: hidden;
}

.bq-bar-fill {
    height: 100%;
    background: @meter;
    transition: width 0.3s ease;

    &.bq-bar--warn {
        background: @warning-border;
    }

    &.bq-bar--danger {
        background: @danger;
    }

    // Over the limit on a plan that bills the excess: informational, not alarming.
    &.bq-bar--over {
        background: @meter-over;
    }
}

// The file pool: three segments of one track. Colours are per bucket and never
// change with the level; the track's ring says how full the pool is.
.bq-bar--stacked {
    display: flex;
    box-sizing: border-box;

    &.bq-bar--warn {
        box-shadow: 0 0 0 1px @warning-border;
    }

    &.bq-bar--danger {
        box-shadow: 0 0 0 1px @danger;
    }

    &.bq-bar--over {
        box-shadow: 0 0 0 1px @meter-over;
    }
}

.bq-bar-seg {
    height: 100%;
    flex: 0 0 auto;
    transition: width 0.3s ease;
}

.bq-bar-seg--host {
    background: @meter;
}

.bq-bar-seg--cloud {
    background: @meter-cloud;
}

.bq-bar-seg--file {
    background: @meter-file;
}

.bq-usage-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem 1rem;
    font-size: 0.72rem;
    color: @muted;
}

.bq-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;

    span {
        color: @ink;
    }
}

// A meter line that reports a block in force (code/plan-copy.ts MeterNote).
.bq-usage-alert {
    color: @danger;
}

.bq-legend-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
}

.bq-plan-cta {
    margin-top: 1rem;
    text-align: right;

    /* Shrink the link to the button it wraps, so nothing but the button takes
       the click. */
    a {
        display: inline-block;
    }
}

/* --- overage (month to date) --- */
.bq-overage {
    margin-top: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid @line;
}

.bq-overage-title {
    margin: 0 0 0.35rem;
    font-size: 0.78rem;
    font-weight: 600;
    color: @muted;
}

.bq-overage-total {
    border-top: 1px solid @line;
    font-weight: 600;

    span:first-child {
        color: @ink;
    }
}

.bq-overage-note {
    margin: 0.5rem 0 0;
    font-size: 0.78rem;
    color: @muted;

    // The hard stop line stands alone under the meters, with no block above it.
    &.bq-overage-note--alone {
        margin-top: 1rem;
    }
}

/* --- toggle rows --- */
.bq-toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.85rem 0;
    border-bottom: 1px solid @line;
    gap: 1rem;

    &:first-of-type {
        border-top: 0;
        padding-top: 0;
    }

    &:last-of-type {
        border-bottom: 0;
        padding-bottom: 0;
    }
}

.bq-toggle-label {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;

    span {
        font-size: 0.88rem;
        font-weight: 500;
    }
}

.bq-toggle-hint {
    font-size: 0.72rem;
    color: @muted;
    font-weight: 400;
    line-height: 1.4;
}

// Ellipsis-safe display of the (possibly long) API key.
.bq-api-key-value {
    display: block;
    max-width: 100%;
    min-height: 1.4em;
    font-family: @mono;
}

.bq-api-key-slot {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    min-height: 1.4em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.bq-api-key-row .bq-toggle-label {
    flex: 1;
    min-width: 0;
}

.bq-danger {
    color: @danger;
    font-size: 0.82rem;
}

/* --- modal (service-specific) --- */
.bq-modal-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid @line;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.9rem;
    outline: none;
    margin-bottom: 0.75rem;
    border-radius: 0;
    transition: border-color 0.15s;

    &::placeholder { color: @placeholder; }
    &:focus { border-color: @ink; }
}

.bq-modal-textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid @line;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.9rem;
    outline: none;
    margin-bottom: 0.75rem;
    border-radius: 0;
    transition: border-color 0.15s;
    resize: vertical;
    min-height: 4rem;
    word-wrap: break-word;
    overflow-x: hidden;
    white-space: pre-wrap;

    &::placeholder { color: @placeholder; }
    &:focus { border-color: @ink; }
    &:disabled { opacity: 0.5; cursor: default; }
}

.btn {
    width: 100%;
}

.bq-link {
    left: -2px;
    position: relative;
}

/* --- responsive --- */
@media (max-width: 600px) {
    .bq-dash-header {
        flex-direction: column;
        gap: 1rem;
    }

    .bq-row {
        flex-wrap: wrap;
        gap: 0.25rem 1rem;
    }

    .bq-row-label {
        min-width: 100%;
    }

    .bq-meta-row {
        flex-direction: column;
        gap: 0.15rem;

        span:last-child {
            text-align: left;
        }

        .bq-meta-copy {
            justify-content: flex-start;
        }
    }
}
</style>
